import * as React from 'react';
import MediaQuery from 'react-responsive';
import cn from 'classnames';

import Dropdown from '../../../../Dropdown';
import { useConfig } from '../../../../context';
import { getCurrency, setCurrency } from '../../../../cache';
import { Currency } from '../../../../enums';
import { useTheme } from '../../../../theme';
import { MOBILE_MIN_WIDTH } from '../../../../utils';
import SlideBottom from '../../../../SlideBottom/components/SlideBottom';
import { useToggleable } from '../../../../hooks';
import { CdnIcon } from '@shared/ui';

interface Props {
	defaultCurrency?: Currency;
	isDisabled?: boolean;
	onSetCurrency?: (code: Currency) => void;
}

const CurrencyComponent: React.FC<Props> = ({ defaultCurrency = getCurrency(), isDisabled, onSetCurrency }) => {
	const { isOpen, toggle, close } = useToggleable(false);
	const [currentCurrency, setCurrencyState] = React.useState(defaultCurrency);

	const theme = useTheme('SearchForm').Currency;

	const currencies = useConfig().SearchForm.selectableCurrencies;

	const currenciesList = React.useMemo(() => {
		return currencies.map(currency => ({
			...currency,
			selected: currency.code === currentCurrency
		}));
	}, [currencies, currentCurrency]);

	const selectedCurrency = React.useMemo(() => {
		const currencyFromList = currenciesList.find(currency => currency.code === currentCurrency);

		return currencyFromList ?? currenciesList[0];
	}, [currenciesList, currentCurrency]);

	const setCurrencyHandler = (code: string) => {
		const currencyCode = code as Currency;

		setCurrency(currencyCode);
		setCurrencyState(currencyCode);
		onSetCurrency?.(currencyCode);

		close();
	};

	const renderList = React.useMemo(() => {
		return (
			<div className={theme.dropdown}>
				{currenciesList.map(currency => {
					return (
						<div
							key={currency.code}
							className={theme.item}
							onClick={() => setCurrencyHandler(currency.code)}
						>
							{currency.symbol && <span data-currency={currency.code}>{currency.symbol}</span>}
							{currency.symbol && currency.code && ' - '}
							{currency.code}
						</div>
					);
				})}
			</div>
		);
	}, [currenciesList]);

	const onToggle = () => {
		if (!isDisabled) {
			toggle();
		}
	};

	return (
		<div className={theme.currency}>
			<button
				className={cn(theme.selector, {
					[theme.selector_disabled]: isDisabled
				})}
				type="button"
				disabled={isDisabled}
				onClick={onToggle}
			>
				<span className={theme.selector__inner}>
					{selectedCurrency.symbol && (
						<span className={theme.selector__symbol} data-currency={selectedCurrency.code}>
							{selectedCurrency.symbol}
						</span>
					)}
					<span>{selectedCurrency.code}</span>
				</span>
				<span className={cn(theme.arrowIcon, { [theme.arrowIcon_flipped]: isOpen })}>
					<CdnIcon icon={'Arrow/Drop_down'} />
				</span>
			</button>

			<MediaQuery minWidth={MOBILE_MIN_WIDTH}>
				<Dropdown isOpen={isOpen} onClose={close} outsideClickIgnoreClass={theme.currency.split(' ')[0]}>
					{renderList}
				</Dropdown>
			</MediaQuery>

			<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
				<SlideBottom isOpen={isOpen} onClose={close}>
					{renderList}
				</SlideBottom>
			</MediaQuery>
		</div>
	);
};

export default CurrencyComponent;
